// components/modals/MeetingsContent.js

import React, { useState, useEffect } from 'react';
import { FiCalendar, FiRefreshCw, FiSearch, FiUpload } from 'react-icons/fi';

import { DataState } from '../../context/DataProvider';
import UploadTranscriptModal from './UploadTranscriptModal';
import MeetingsList from './MeetingsList';
import { useAccess } from '../../services/access';
import * as meetingsApi from '../../services/meetingsApi';
import { fetchProjectMembers } from '../../services/api';

// The current project's members, shaped like the attendee picker's users.
// Everyone may list them (project:member:view); the full user directory
// needs user_management:view, which meeting organisers such as leads lack.
const projectMembersAsUsers = async (token) => {
  const { project_id: projectId } = JSON.parse(atob(token.split('.')[1]));
  const { data } = await fetchProjectMembers(token, projectId);
  return (data || []).map((m) => ({
    id: m.user_id,
    displayName: m.name || m.email,
    email: m.email,
  }));
};

const TABS = [
  ['all', 'All meetings'],
  ['teams', 'Teams'],
  ['manual', 'Uploaded'],
];

const MeetingsContent = () => {
  const { userGoogleId: accessToken } = DataState();
  // Uploading, retrying and removing need meeting:manage (Lead and above).
  const { can } = useAccess();
  const canManage = can('meeting:manage');
  const [allUsers, setAllUsers] = useState([]);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [tab, setTab] = useState('all');
  const [listKey, setListKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // The user list only feeds the upload's attendee picker. The Team
  // Directory may be empty or partial, so project members not in it are
  // added after it (matched by email).
  useEffect(() => {
    if (!canManage) return;
    Promise.all([
      can('user_management:view')
        ? meetingsApi.listUsers(accessToken).catch(() => [])
        : [],
      projectMembersAsUsers(accessToken).catch(() => []),
    ]).then(([directory, members]) => {
      const known = new Set(directory.map((u) => u.email?.toLowerCase()));
      setAllUsers([
        ...directory,
        ...members.filter((m) => !known.has(m.email?.toLowerCase())),
      ]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, canManage]);

  return (
    <div className="flex h-full max-h-screen flex-col p-1">
      {isUploadOpen && (
        <UploadTranscriptModal
          accessToken={accessToken}
          users={allUsers}
          onClose={() => setIsUploadOpen(false)}
          onUploaded={() => {
            setIsUploadOpen(false);
            setListKey((k) => k + 1);
          }}
        />
      )}

      <div className="mb-3 flex flex-col items-center justify-between gap-3 px-1 md:flex-row">
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold text-zinc-100">
            <FiCalendar className="h-4 w-4 text-purple-500" />
            Meetings
          </h3>
          <p className="text-[10px] text-zinc-500">
            Teams meetings appear here by themselves once they have a
            transcript. You see only the meetings you were in.
          </p>
        </div>

        <div className="flex w-full items-center gap-2 md:w-auto">
          <button
            type="button"
            onClick={() => setListKey((k) => k + 1)}
            className="rounded border border-zinc-700 bg-zinc-800/50 p-1.5 text-zinc-400 transition-colors hover:bg-zinc-700 hover:text-zinc-100"
            title="Refresh"
            aria-label="Refresh"
          >
            <FiRefreshCw className="h-3.5 w-3.5" />
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => setIsUploadOpen(true)}
              className="flex items-center gap-1.5 rounded bg-[#5652CF] px-2.5 py-1.5 text-[11px] font-medium text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#4642bf]"
            >
              <FiUpload className="h-3 w-3" /> Upload transcript
            </button>
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-zinc-800 bg-[#121214] shadow-sm">
        <div className="flex items-center gap-3 border-b border-zinc-800 bg-zinc-900/30 px-3 py-2">
          <div className="relative max-w-xs flex-1">
            <FiSearch className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search meetings..."
              aria-label="Search meetings"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded border border-zinc-700 bg-zinc-900 py-1.5 pl-8 pr-2 text-[11px] text-zinc-200 transition-all placeholder:text-zinc-600 focus:border-[#5652CF] focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50"
            />
          </div>
          <div
            className="ml-auto flex rounded border border-zinc-700 text-[10px]"
            role="tablist"
          >
            {TABS.map(([id, label]) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={`px-2 py-1 ${tab === id ? 'bg-[#5652CF] text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="custom-scrollbar flex-1 space-y-2 overflow-auto bg-zinc-900/10 p-2">
          <MeetingsList
            accessToken={accessToken}
            canManage={canManage}
            searchQuery={searchQuery}
            kind={tab === 'all' ? null : tab}
            refreshKey={listKey}
          />
        </div>
      </div>
    </div>
  );
};

export default MeetingsContent;
